import React from 'react';
import { Box } from '@mui/material';
import {
  isLikelyStrapiDocumentId,
  parseStoredParameterUjiDocumentIds,
  type StrapiParameterUjiMini,
} from '../api/strapiCrm';
import { ChemInline, ChemParameterLabel } from './ChemInline';

export type ChemParameterUjiLookupRow = { nama_parameter: string | null; simbol: string | null };

export type ChemParameterUjiCellProps = {
  raw?: string | null | undefined;
  /** Populated M2M rows — preferred when API returns full `parameter_ujis` list. */
  items?: ReadonlyArray<StrapiParameterUjiMini>;
  puLookup: ReadonlyMap<string, ChemParameterUjiLookupRow>;
  /** Separator between parameters (default middle dot). */
  separator?: string;
  emptyFallback?: React.ReactNode;
};

function renderParameterUjiNode(
  row: ChemParameterUjiLookupRow,
  key: string,
): React.ReactNode {
  const n = (row.nama_parameter ?? '').trim();
  const sym = (row.simbol ?? '').trim();
  if (!n && !sym) {
    return <span key={key}>{key.length > 10 ? `${key.slice(0, 10)}…` : key}</span>;
  }
  return (
    <span key={key}>
      <ChemParameterLabel nama={row.nama_parameter} simbol={row.simbol} />
    </span>
  );
}

/** Ringkasan `parameter_uji` (id `;`/`,`-pisah atau teks lama) dengan `ChemInline` per segmen. */
export function ChemParameterUjiCell({
  raw,
  items,
  puLookup,
  separator = ' · ',
  emptyFallback = '—',
}: ChemParameterUjiCellProps) {
  const nodes: React.ReactNode[] = [];

  if (items?.length) {
    for (const pu of items) {
      const key = pu.documentId.trim();
      if (!key) continue;
      const fromLookup = puLookup.get(key);
      const row: ChemParameterUjiLookupRow = fromLookup ?? {
        nama_parameter: pu.nama_parameter,
        simbol: pu.simbol,
      };
      nodes.push(renderParameterUjiNode(row, key));
    }
  } else {
    const s = typeof raw === 'string' ? raw.trim() : '';
    if (!s) return <>{emptyFallback}</>;
    const tokens = parseStoredParameterUjiDocumentIds(s).filter(Boolean);
    if (!tokens.length) return <>{emptyFallback}</>;
    const allIds = tokens.every((t) => isLikelyStrapiDocumentId(t));

    if (allIds) {
      for (const id of tokens) {
        const key = id.trim();
        const row = puLookup.get(key);
        if (!row) {
          nodes.push(<span key={key}>{key.length > 10 ? `${key.slice(0, 10)}…` : key}</span>);
          continue;
        }
        nodes.push(renderParameterUjiNode(row, key));
      }
    } else {
      tokens.forEach((t, i) => {
        nodes.push(
          <span key={`${i}-${t.slice(0, 24)}`}>
            <ChemInline text={t} />
          </span>,
        );
      });
    }
  }

  if (!nodes.length) return <>{emptyFallback}</>;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline',
        wordBreak: 'break-word',
        '& .katex-html': { whiteSpace: 'normal' },
      }}
    >
      {nodes.map((node, i) => (
        <React.Fragment key={i}>
          {i > 0 ? separator : null}
          {node}
        </React.Fragment>
      ))}
    </Box>
  );
}
